import Box from '@mui/material/Box';

import { patternContainerSx, type SectionPatternName, sectionPatternSx } from '@/theme';

type Props = { name: SectionPatternName };

/**
 * The decorative layer behind a page band (§52 backdrops).
 *
 * It is deliberately the dumbest thing in the set: it takes a name, paints the
 * recipe that name maps to, and holds no state, no measurement and no
 * lifecycle. Every judgement about what a pattern looks like lives in
 * `theme/patterns`; every judgement about which band wears one lives in the
 * page. This only puts the two together.
 *
 * ## Why two nodes
 *
 * The motif's density is chosen by container query, and a container query
 * never matches the element that declares the container — so the box being
 * measured and the box being painted have to be different nodes. The outer one
 * declares the container and nothing else; the inner one carries every
 * property that has an appearance. Measuring here rather than on `Section`
 * itself is deliberate: `container-type` brings layout containment with it,
 * and putting that on the band would change the box the page's own content
 * lays out in — new block formatting context, a different containing block for
 * positioned descendants, margins that no longer collapse through. An empty
 * absolutely positioned layer has no content for containment to affect, so
 * here it costs nothing and cannot reach the page.
 *
 * `aria-hidden` and `pointerEvents: none` because it is texture: it is not in
 * the accessibility tree and it never intercepts a click meant for the content
 * stacked over it. `zIndex: 0` against the content's `1` rather than a
 * negative index, so the layer does not need its parent to isolate itself to
 * stay off the page background.
 */
export function SectionPattern({ name }: Props) {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        ...patternContainerSx,
      }}
    >
      <Box sx={[{ position: 'absolute', inset: 0 }, ...sectionPatternSx(name)]} />
    </Box>
  );
}
